/**
 * @file 教材相关 API（对齐「思犇优学」`/common/textbook/ddl` 文档）
 * @description 按科目查教材下拉；教材携带适用配置（阶段/年级/学期/学习力等级，
 * 多值逗号串），前端据此派生科目详情的级联选项。
 *
 * @whenToUse 学员科目详情中「科目 → 教材 → 配置项」级联选择、列表教材筛选。
 * @whenNotToUse 教材管理页（后台无此页面）。
 */

import { getPage } from '@/lib/client.ts'

/** 教材适用配置（字段为多值逗号串） */
export type TextbookConfig = {
  /** 主键 */
  id: string
  /** 教材 ID */
  textbookId: string
  /** 教材名称（联查展示用） */
  textbookName: string
  /** 学科（多值逗号分隔） */
  subject: string
  /** 教育阶段（多值逗号分隔） */
  educationStage: string
  /** 年级（多值逗号分隔） */
  grade: string
  /** 学期（多值逗号分隔） */
  semester: string
  /** 学习力等级（多值逗号分隔） */
  learningGrade: string
}

/** 教材下拉项（含适用配置） */
export type TextbookDdlItem = {
  /** 教材 ID（int64 雪花 id，JSON 序列化为字符串） */
  id: string
  /** 教材名称 */
  name: string
  /** 教材适用配置列表 */
  textbookConfigList: TextbookConfig[]
}

/** 教材下拉查询参数 */
export type TextbookDdlParams = {
  /** 科目（按子表配置过滤，传单值） */
  subject?: string
  /** 每页条数；不传时后端默认值过小，按需传大 */
  pageSize?: number
}

/**
 * 查询教材下拉（按科目过滤，含适用配置）。
 * @param params 过滤条件
 * @returns 教材列表（截掉分页壳）
 */
export async function fetchTextbookDdl(params?: TextbookDdlParams) {
  const { rows } = await getPage<TextbookDdlItem>('/common/textbook/ddl', {
    params,
  })
  return rows
}

/**
 * 多值逗号串拆为去重值集合
 * @param csv 形如 `"junior_high,senior_high"` 的字符串
 */
export function splitCsv(csv: string): string[] {
  return Array.from(
    new Set(
      csv
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  )
}
